import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { absMediaUrl } from "../utils/mediaUrl";
import { copyTextToClipboard } from "../lib/clipboard";
import { COMPANY_INSTAGRAM, buildWelcomeDm, cleanHandle, igDmUrl, igProfileUrl } from "../utils/welcomeDm";
import { withQuietRetry } from "../utils/adminRetry";
import AdminLoadFailed from "./AdminLoadFailed";
import "./AdminApprovalsTab.css";

// Approvals tab (THE IG GATE): new talent waiting for the founder's yes.
// A new talent takes no payments until Approve is tapped here. Oldest first;
// the server only lists talent who finished bank setup and gave an Instagram
// handle or a link. Approve / Decline each send the talent one email.
//
// After Approve, a card asks the founder to send the welcome DM by hand:
// one tap copies the message and opens the talent's Instagram DM thread.
// Instagram does not let a link pick which logged-in account sends, so the
// card reminds the founder to be on the company account first.

interface Waiting {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  instagram?: string;
  links?: string[];
  bankCountry?: string;
  /** The picked country as a code (server), to compare with the ID's. */
  bankCountryCode?: string | null;
  /** MassPay talent: the ID check result and the country printed on the ID. */
  masspay?: { kycStatus?: string | null; kycDocCountry?: string | null } | null;
  createdAt: string;
  /** Set when they signed in with Instagram: Meta vouched for this handle. */
  instagramVerification?: { username?: string; followersCount?: number | null; verifiedAt?: string } | null;
}

// "ID" → "Indonesia" (English: the founder reads the board in English).
const regionName = (code: string) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
};

const followers = (n?: number | null) =>
  typeof n === "number" && n >= 0 ? `${n.toLocaleString()} follower${n === 1 ? "" : "s"}` : null;

function waitingFor(iso: string) {
  const h = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3600000));
  if (h < 1) return "just now";
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

function WelcomeDmCard({ talent, onDone }: { talent: Waiting; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const handle = cleanHandle(talent.instagram);
  const message = buildWelcomeDm(talent.name);
  // Copy synchronously inside the tap so iOS keeps the user-gesture grant;
  // the <a> then navigates natively (no window.open, no popup blocker).
  const copy = () => {
    void copyTextToClipboard(message).then(() => setCopied(true));
  };
  return (
    <div className="adm-welcome-dm" role="status">
      <p className="adm-welcome-dm-title">✅ {talent.name} is approved. Now send the welcome DM.</p>
      <span className="adm-welcome-dm-warn">
        Instagram opens on whichever account you used last. Switch to <strong>@{COMPANY_INSTAGRAM}</strong> in the Instagram app before you send, not your personal.
      </span>
      <pre className="adm-welcome-dm-preview">{message}</pre>
      <div className="adm-welcome-dm-actions">
        {handle ? (
          <a className="adm-welcome-dm-open" href={igDmUrl(handle)} target="_blank" rel="noopener noreferrer" onClick={copy}>
            Copy message &amp; open @{handle}
          </a>
        ) : (
          <button type="button" className="adm-btn-sm adm-btn-green-sm" onClick={copy}>
            Copy message
          </button>
        )}
        <button type="button" className="adm-btn-sm" onClick={copy}>
          {copied ? "Copied ✓" : "Copy only"}
        </button>
        <button type="button" className="adm-btn-sm" onClick={onDone}>
          Done
        </button>
      </div>
      <p className="muted-text adm-fs-72" style={{ margin: "8px 0 0" }}>
        {handle ? (
          <>
            Paste in the thread and send. Didn't open a chat?{" "}
            <a href={igProfileUrl(handle)} target="_blank" rel="noopener noreferrer">Open their profile</a> and tap Message.
          </>
        ) : (
          <>No Instagram handle on file. Send it by email ({talent.email}) or through their link.</>
        )}
      </p>
    </div>
  );
}

export default function AdminApprovalsTab({ onCount }: { onCount: (n: number) => void }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<Waiting[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [justApproved, setJustApproved] = useState<Waiting | null>(null);

  const load = useCallback(() => {
    setFailed(false);
    withQuietRetry(() => adminApi.get("/approvals"))
      .then((r) => {
        const list: Waiting[] = r.data?.items || [];
        setItems(list);
        onCount(list.length);
      })
      .catch(() => setFailed(true));
  }, [onCount]);

  useEffect(load, [load]);

  const decide = async (u: Waiting, action: "approve" | "decline") => {
    if (action === "decline" && !window.confirm(`Decline ${u.name}? They keep their free page; paid requests stay off. They get the "couldn't verify" email.`)) return;
    setBusyId(u._id);
    try {
      await adminApi.patch(`/users/${u._id}/${action}`);
      const next = (items || []).filter((x) => x._id !== u._id);
      setItems(next);
      onCount(next.length);
      if (action === "approve") setJustApproved(u);
    } catch (err: any) {
      alert(err.response?.data?.msg || "That didn't go through. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  // Never "Nobody is waiting." for a list that didn't load: someone may be.
  if (items === null) return failed ? <AdminLoadFailed what="approvals" onRetry={load} /> : <p className="muted-text">Loading…</p>;

  const card = justApproved && <WelcomeDmCard key={justApproved._id} talent={justApproved} onDone={() => setJustApproved(null)} />;

  if (items.length === 0) {
    return (
      <section className="fade-in">
        {card}
        <p className="muted-text">Nobody is waiting.</p>
      </section>
    );
  }

  return (
    <section className="fade-in">
      {card}
      <p className="muted-text adm-fs-88 adm-mb-14">Oldest first. Tap a handle to open Instagram.</p>
      <div className="adm-table-wrapper">
        <table className="adm-table-light">
          <thead>
            <tr>
              <th>Talent</th>
              <th>Instagram</th>
              <th>Country</th>
              <th>Waiting</th>
              <th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const avatarSrc = absMediaUrl(u.avatarUrl);
              const ig = cleanHandle(u.instagram);
              // The badge only counts while Meta's handle is still the one on
              // the profile (the server voids it on any other edit).
              const vName = (u.instagramVerification?.username || "").trim();
              const verified = !!vName && !!ig && vName.toLowerCase() === ig.toLowerCase();
              const link = (u.links || []).find((l) => /^https?:\/\//i.test(l));
              return (
                <tr key={u._id}>
                  <td>
                    <div className="dj-cell clickable" onClick={() => navigate(`/admin/dj/${u._id}`)}>
                      {avatarSrc ? (
                        <img src={avatarSrc} className="dj-cell-avatar" alt="" />
                      ) : (
                        <div className="dj-cell-placeholder">{(u.name || "?")[0].toUpperCase()}</div>
                      )}
                      <div>
                        <span className="dj-link-text adm-fs-88 adm-semibold">{u.name}</span>
                        <div className="muted-text adm-fs-72">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {ig ? (
                      <>
                        <a href={igProfileUrl(ig)} target="_blank" rel="noopener noreferrer" className="adm-semibold">
                          @{ig}
                        </a>
                        <div className="adm-fs-72">
                          {verified ? (
                            <span className="pill adm-pill-sm adm-pill-green">
                              Verified{followers(u.instagramVerification?.followersCount) ? ` · ${followers(u.instagramVerification?.followersCount)}` : ""}
                            </span>
                          ) : (
                            <span className="muted-text">Typed, not verified</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="pill adm-pill-sm adm-pill-amber">No Instagram</span>
                        {link && (
                          <div className="adm-fs-72">
                            <a href={link} target="_blank" rel="noopener noreferrer">{link.replace(/^https?:\/\//, "")}</a>
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="muted-text">
                    {u.bankCountry || "—"}
                    {/* MassPay: the country on their ID. Red when it isn't the
                        country they picked — someone in a Stripe country
                        claiming a MassPay one, or a typo worth a look. */}
                    {u.masspay?.kycDocCountry && (
                      <div className="adm-fs-72">
                        {u.bankCountryCode && u.masspay.kycDocCountry.toUpperCase() !== u.bankCountryCode.toUpperCase() ? (
                          <span className="pill adm-pill-sm adm-pill-red">ID from {regionName(u.masspay.kycDocCountry)}</span>
                        ) : (
                          <span>ID from {regionName(u.masspay.kycDocCountry)}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="muted-text">{waitingFor(u.createdAt)}</td>
                  <td>
                    <div className="action-btn-group">
                      <button className="adm-btn-sm adm-btn-red-sm" disabled={busyId === u._id} onClick={() => decide(u, "decline")}>
                        Decline
                      </button>
                      <button className="adm-btn-sm adm-btn-green-sm" disabled={busyId === u._id} onClick={() => decide(u, "approve")}>
                        Approve
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="muted-text adm-fs-72">
        Only talent who finished bank setup can be approved. Approve turns payments on, sends the "You're ready to earn" email, and hands you the welcome DM to send from @{COMPANY_INSTAGRAM}.
      </p>
    </section>
  );
}
